import { getImpersonationToken } from '@/lib/impersonation-token';

/**
 * PostHog identity for a tab that is impersonating a customer.
 *
 * posthog-js keeps its distinct_id AND its session id in `localStorage` + a
 * cookie, shared by every tab on the origin. Impersonation is tab-scoped
 * (`impersonation-token.ts`), but analytics was not: the impersonating tab
 * carried on in the admin's own session and merely `identify()`-ed as the
 * customer mid-recording. One replay ran from the admin's own usage, through
 * the admin terminal, into the customer's dashboard and back out — and the
 * admin pages after "Back to admin panel" were recorded AS the customer, since
 * that identify had been written to the shared store and `/admin` never
 * re-identified. In production a single session spanned 9 hours and 12
 * identities.
 *
 * So an impersonating tab runs PostHog on its own `sessionStorage`
 * persistence: a fresh distinct_id and session id — so a new replay — from the
 * first load after `setImpersonationToken`, kept across reloads while the
 * impersonation lasts, and never written where the admin's other tabs read.
 * Every load that is NOT impersonating discards it, so the replay ends with the
 * impersonation however it ended (stop, expiry, sign-out) and the next one
 * starts clean.
 */
const PERSISTENCE_NAME = 'borradh_impersonation';

/** The `sessionStorage` key posthog-js derives: `ph_` + `persistence_name`. */
export const IMPERSONATION_POSTHOG_STORAGE_KEY = `ph_${PERSISTENCE_NAME}`;

/** Spread into `posthog.init` config for an impersonating tab. */
export const IMPERSONATION_POSTHOG_PERSISTENCE = {
  persistence: 'sessionStorage',
  persistence_name: PERSISTENCE_NAME,
} as const;

/** Read once at PostHog init: the persistence mode cannot change after it. */
export const isImpersonatingTab = (): boolean =>
  getImpersonationToken() !== undefined;

/** Drop the last impersonation's PostHog identity and session from this tab. */
export const discardImpersonationPostHogState = (): void => {
  try {
    window.sessionStorage.removeItem(IMPERSONATION_POSTHOG_STORAGE_KEY);
  } catch {
    // Storage unavailable — then posthog-js could not have written it either.
  }
};
